import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useConfigurations } from "@via/api/configuration.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { PERMISSION, USER_ROLES } from "@via/utils/constants.tsx";
import { createProjectOptions } from "@via/utils/form-validators.tsx";
import { classNames, getPermissionType } from "@via/utils/helpers.tsx";
import { PermissionType, type CreateProjectRequestBody } from "common";
import { ChangeEvent, useEffect, type JSX, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateProject } from "@via/pages/projects/projects.api.tsx";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";

export default function CreateProjectForm({
  setOpen,
}: {
  setOpen: (open: boolean) => void;
}): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: configurations } = useConfigurations();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const defaultPermission =
    isAdmin && configurations?.ENABLE_SHARE_WITH_EVERYONE_PROJECT
      ? PermissionType.Public
      : PermissionType.UserOwned;

  const {
    mutate: createProject,
    data: createProjectData,
    isPending: isCreateProjectLoading,
    isSuccess: isCreateProjectSuccess,
  } = useCreateProject();

  // Only an admin may share a project publicly.
  const isPublicOptionDisabled = !isAdmin;

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreateProjectRequestBody>({
    mode: "onChange",
    defaultValues: {
      name: "",
      summary: "",
      permission: defaultPermission,
      groupId: undefined,
    },
  });

  const permission = watch(PERMISSION);

  useEffect(() => {
    if (permission !== PermissionType.GroupShared) {
      setValue("groupId", undefined); // Reset groupId when permission changes
    }
  }, [setValue, permission]);

  useEffect(() => {
    if (createProjectData) {
      const { id } = createProjectData;
      navigate(`/projects/${id}`);
      setOpen(false);
    }
  }, [isCreateProjectSuccess, setOpen]);

  const handleCreateProject = (payload: CreateProjectRequestBody): void => {
    createProject(payload);
  };

  const handlePermissionChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const permission = getPermissionType(e.target.value);
    setValue(PERMISSION, permission);
  };

  // Function to render group selection based on permission
  const renderGroupSelection = (): ReactNode => {
    return permission === PermissionType.GroupShared ? (
      <UserGroupSelectComponent
        control={control}
        name="groupId"
        rules={createProjectOptions.groupId}
        menuPlacement="top"
        errors={errors}
      />
    ) : null;
  };

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleCreateProject)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              New Project
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                id="create-project-exit"
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={() => {
                  setOpen(false);
                }}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Get started by filling in the information below to create your new
              project.
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="name"
                label="Project name"
                showRequiredField={true}
                placeholder="Name your Project"
                validations={createProjectOptions.name}
              />
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Description
                </label>
                <div className="mt-2">
                  <textarea
                    {...register("summary")}
                    rows={4}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder={"Enter a Project description"}
                  />
                </div>
              </div>
              <fieldset>
                <legend className="text-sm font-medium leading-6 text-gray-900">
                  Permission
                </legend>
                <div className="mt-2 space-y-4">
                  <div
                    className={classNames(
                      "relative flex items-start",
                      isPublicOptionDisabled ? "opacity-50" : ""
                    )}>
                    <div className="absolute flex h-6 items-center">
                      <input
                        {...register(PERMISSION)}
                        id="permission-public"
                        disabled={isPublicOptionDisabled}
                        onChange={handlePermissionChange}
                        value={PermissionType.Public}
                        aria-describedby="permission-public-description"
                        type="radio"
                        className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    </div>
                    <div className="pl-7 text-sm leading-6">
                      <label
                        htmlFor="permission-public"
                        className="font-medium text-gray-900">
                        Public access
                      </label>
                      <p
                        id="permission-public-description"
                        className="text-gray-500">
                        Everyone with the link will see this project.
                        {!isAdmin &&
                          " Only administrators can share projects publicly."}
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="relative flex items-start">
                      <div className="absolute flex h-6 items-center">
                        <input
                          {...register(PERMISSION)}
                          id="permission-group"
                          onChange={handlePermissionChange}
                          value={PermissionType.GroupShared}
                          aria-describedby="permission-group-description"
                          type="radio"
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                      <div className="pl-7 text-sm leading-6">
                        <label
                          htmlFor="permission-group"
                          className="font-medium text-gray-900">
                          Private to group members
                        </label>
                        <p
                          id="permission-group-description"
                          className="text-gray-500">
                          Only members of this project would be able to access.
                        </p>
                      </div>
                    </div>
                  </div>
                  {renderGroupSelection()}
                  <div>
                    <div className="relative flex items-start">
                      <div className="absolute flex h-6 items-center">
                        <input
                          {...register(PERMISSION)}
                          id="permission-private"
                          onChange={handlePermissionChange}
                          value={PermissionType.UserOwned}
                          aria-describedby="permission-private-description"
                          type="radio"
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                      <div className="pl-7 text-sm leading-6">
                        <label
                          htmlFor="permission-private"
                          className="font-medium text-gray-900">
                          Private to you
                        </label>
                        <p
                          id="permission-private-description"
                          className="text-gray-500">
                          You are the only one able to access this project.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="create-project-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => {
            setOpen(false);
          }}>
          Cancel
        </button>
        <button
          id="create-project-save"
          type="submit"
          disabled={isCreateProjectLoading}
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isCreateProjectLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
